import React from 'react';

import { UxContext } from '@client/context/UxContext';
import { usePageStore } from '@client/store/pageStore/context/pageContext';
import i18n from '@client/utils/i18n';
import { SpectrogramPoint } from '@ledium/types';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import { Button, Grid, Tooltip, Typography } from '@mui/material';

import useCreateLightRecipeFromSpectrum from '../Hooks/useCreateLightRecipeFromSpectrum';

const SpectrumMixerCreateLightRecipe = () => {
    const {
        pageState: { formData }
    } = usePageStore();
    const { handleAlert } = React.useContext(UxContext)!;
    const { findOrCreate, assignRecipe } = useCreateLightRecipeFromSpectrum();

    if (formData.readOnly) {
        return null;
    }

    const aggregatedSpectrogram = formData.aggregatedSpectrogram as SpectrogramPoint[] | undefined;
    const hasName = Boolean(formData.name);
    const hasIntensity = Boolean(aggregatedSpectrogram && aggregatedSpectrogram.some((p) => p.irradiance > 0));
    const isDisabled = !hasName || !hasIntensity;

    const tooltipContent = isDisabled ? (
        <React.Fragment>
            <Typography variant="body2" sx={{ mb: 0.5 }}>
                With this button you can create a light recipe directly from your spectrum mix. The following requirements are not yet met:
            </Typography>
            <ul style={{ margin: 0, paddingLeft: 16 }}>
                {!hasName && <li>Provide a name for this mix</li>}
                {!hasIntensity && <li>Set at least one channel to a non-zero intensity</li>}
            </ul>
        </React.Fragment>
    ) : (
        ''
    );

    async function handleClick() {
        const result = await findOrCreate();
        if (!result) {
            return;
        }

        const { recipe, wasCreated } = result;

        if (wasCreated) {
            handleAlert({
                title: i18n('spectrum_mix_create_light_recipe_created_title') as string,
                message: i18n('spectrum_mix_create_light_recipe_created_message', <strong>{recipe.name}</strong>),
                okText: 'Yes',
                cancelText: 'No',
                okCallback: () => assignRecipe(recipe)
            });
        } else {
            handleAlert({
                title: i18n('spectrum_mix_create_light_recipe_exists_title') as string,
                message: i18n('spectrum_mix_create_light_recipe_exists_message', <strong>{recipe.name}</strong>),
                okText: 'Yes',
                cancelText: 'No',
                okCallback: () => assignRecipe(recipe)
            });
        }
    }

    return (
        <Grid item xs={12} sx={{ textAlign: 'center' }}>
            <Tooltip title={tooltipContent}>
                <span>
                    <Button variant="outlined" startIcon={<AddCircleOutlineIcon />} onClick={handleClick} disabled={isDisabled}>
                        Create Light Recipe from This Mix
                    </Button>
                </span>
            </Tooltip>
        </Grid>
    );
};

export default SpectrumMixerCreateLightRecipe;
